import type {
  AutomationCohortVariant,
  AutomationSequenceSummary,
  CollabAutomation,
  CollabTriggerType,
  CreatorTriggerType,
} from "@unbox/api";
import { ChevronDown, Edit01, Zap } from "@untitledui/icons";
import { useEffect, useState } from "react";
import {
  useDeleteCollaborationSequenceOverride,
  useTargetCollaborationAutomations,
  useUpsertCollaborationSequenceOverride,
} from "@/api/collaborations";
import { useSequence } from "@/api/sequences";
import {
  useDeleteSmartCollabGroupSequenceOverride,
  useSmartCollabGroupAutomations,
  useUpsertSmartCollabGroupSequenceOverride,
} from "@/api/smart-collab-groups";
import { ModePill } from "@/components/automations/mode-pill";
import { Button } from "@/components/base/buttons/button";
import { Tooltip, TooltipTrigger } from "@/components/base/tooltip/tooltip";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { TRIGGER_ICON } from "@/pages/sequences/automation-builder";
import { useCollabTriggerTypes } from "@/pages/sequences/collab-trigger-types";
import { SequenceStepsPreview, TRIGGER_EVENT_LABELS } from "@/pages/sequences/sequence-steps-preview";
import { error as toastError } from "@/providers/ToastProvider";
import { cx } from "@/utils/cx";
import { pluralize } from "@/utils/text";

export type AutomationTriggersScope =
  | { kind: "group"; groupId: string }
  | { kind: "collaboration"; collaborationId: string };

type ViewAutomationTriggersModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  scope: AutomationTriggersScope;
  onEdit?: () => void;
  editDisabledReason?: string;
};

type ToggleValue = "disabled" | "enabled";

const GROUP_MANAGED_REASON = "Managed by the smart group";
const NO_AUTOMATION_REASON = "No automation is set up for this trigger yet";

const CohortChip = ({ name, color }: { name: string; color: string }) => (
  <span className="inline-flex items-center gap-1.5 rounded-full bg-brand-50 px-3 py-1 text-sm font-medium text-brand-800">
    <span className="size-2 rounded-full" style={{ backgroundColor: color }} />
    {name}
  </span>
);

const SequenceSteps = ({ sequenceId }: { sequenceId: string }) => {
  const { data: sequence } = useSequence(sequenceId);
  if (!sequence) return <p className="text-sm text-tertiary">Loading steps…</p>;
  return <SequenceStepsPreview type="creator" steps={sequence.steps} />;
};

const VariantRow = ({
  sequence,
  cohorts,
}: {
  sequence: AutomationSequenceSummary;
  cohorts: AutomationCohortVariant["cohorts"] | null;
}) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="rounded-xl border border-secondary bg-unbox-cream-25">
      <button
        type="button"
        onClick={() => setExpanded((value) => !value)}
        className="flex w-full items-center justify-between gap-3 p-4"
      >
        <span className="flex flex-wrap items-center gap-2">
          {cohorts ? (
            cohorts.map((cohort) => <CohortChip key={cohort.id} name={cohort.name} color={cohort.color} />)
          ) : (
            <span className="rounded-full bg-brand-50 px-3 py-1 text-sm font-medium text-brand-800">
              All other creators
            </span>
          )}
          <span className="size-1 rounded-full bg-quaternary" />
          <span className="text-sm font-medium text-secondary">{pluralize(sequence.stepCount, "step", "steps")}</span>
        </span>
        <ChevronDown className={cx("size-5 shrink-0 text-quaternary transition-transform", expanded && "rotate-180")} />
      </button>
      <div
        className={cx(
          "grid transition-[grid-template-rows] duration-200 ease-out",
          expanded ? "grid-rows-[1fr]" : "grid-rows-[0fr]",
        )}
      >
        <div className="min-h-0 overflow-hidden">
          {expanded && (
            <div className="px-4 pb-4">
              <SequenceSteps sequenceId={sequence.id} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const TriggerSection = ({
  triggerType,
  automation,
  enabled,
  onToggle,
}: {
  triggerType: CollabTriggerType;
  automation: CollabAutomation | undefined;
  enabled: boolean;
  onToggle: (next: boolean) => void;
}) => {
  const TriggerIcon = TRIGGER_ICON[triggerType as CreatorTriggerType] ?? Zap;
  const hasAutomation = !!automation?.sequence;
  const toggleDisabledReason = automation?.groupManaged
    ? GROUP_MANAGED_REASON
    : !hasAutomation
      ? NO_AUTOMATION_REASON
      : undefined;
  const showCohortVariants = automation?.source === "default" || automation?.source === null;

  const toggle = (
    <ModePill<ToggleValue>
      options={[
        { value: "disabled", label: "Disabled" },
        { value: "enabled", label: "Enabled" },
      ]}
      value={enabled ? "enabled" : "disabled"}
      onChange={(next) => onToggle(next === "enabled")}
      isDisabled={!!toggleDisabledReason}
    />
  );

  return (
    <section className="flex flex-col gap-4 rounded-xl bg-unbox-cream-50 p-4">
      <div className="flex items-center gap-2.5">
        <TriggerIcon className="size-5 shrink-0 text-primary" />
        <span className="flex-1 text-sm font-semibold text-primary">
          {TRIGGER_EVENT_LABELS[triggerType] ?? triggerType}
        </span>
        {toggleDisabledReason ? (
          <Tooltip title={toggleDisabledReason}>
            <TooltipTrigger>{toggle}</TooltipTrigger>
          </Tooltip>
        ) : (
          toggle
        )}
      </div>

      {automation?.sequence && <VariantRow sequence={automation.sequence} cohorts={null} />}
      {showCohortVariants &&
        automation?.cohortVariants.map((variant) => (
          <VariantRow key={variant.sequence.id} sequence={variant.sequence} cohorts={variant.cohorts} />
        ))}
    </section>
  );
};

export const ViewAutomationTriggersModal = ({
  open,
  onOpenChange,
  scope,
  onEdit,
  editDisabledReason,
}: ViewAutomationTriggersModalProps) => {
  const triggerTypes = useCollabTriggerTypes();
  const collaborationAutomations = useTargetCollaborationAutomations(
    scope.kind === "collaboration" ? scope.collaborationId : undefined,
    { enabled: open },
  );
  const groupAutomations = useSmartCollabGroupAutomations(scope.kind === "group" ? scope.groupId : undefined, {
    enabled: open,
  });
  const {
    data: automations,
    isLoading,
    isError,
    refetch,
  } = scope.kind === "group" ? groupAutomations : collaborationAutomations;
  const [enabledByTrigger, setEnabledByTrigger] = useState<Partial<Record<CollabTriggerType, boolean>>>({});

  useEffect(() => {
    setEnabledByTrigger(Object.fromEntries((automations ?? []).map((a) => [a.triggerType, a.enabled])));
  }, [automations]);

  const upsertCollabOverride = useUpsertCollaborationSequenceOverride();
  const deleteCollabOverride = useDeleteCollaborationSequenceOverride();
  const upsertGroupOverride = useUpsertSmartCollabGroupSequenceOverride();
  const deleteGroupOverride = useDeleteSmartCollabGroupSequenceOverride();

  const saveToggle = async (triggerType: CollabTriggerType, enabled: boolean) => {
    setEnabledByTrigger((prev) => ({ ...prev, [triggerType]: enabled }));
    try {
      if (scope.kind === "group") {
        if (enabled) await deleteGroupOverride.mutateAsync({ groupId: scope.groupId, triggerType });
        else await upsertGroupOverride.mutateAsync({ groupId: scope.groupId, triggerType, enabled: false });
      } else if (enabled) {
        await deleteCollabOverride.mutateAsync({ collaborationId: scope.collaborationId, triggerType });
      } else {
        await upsertCollabOverride.mutateAsync({ collaborationId: scope.collaborationId, triggerType, enabled: false });
      }
    } catch (e) {
      setEnabledByTrigger((prev) => ({ ...prev, [triggerType]: !enabled }));
      toastError("Couldn't update automation", { description: e instanceof Error ? e.message : undefined });
    }
  };

  const editButton = (
    <Button
      color="secondary"
      iconLeading={Edit01}
      isDisabled={!!editDisabledReason || !onEdit}
      onClick={() => {
        onOpenChange(false);
        onEdit?.();
      }}
    >
      Edit
    </Button>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="gap-6 bg-unbox-cream-25 p-6 sm:max-w-[839px]">
        <DialogHeader>
          <DialogTitle className="text-lg font-semibold text-secondary">View Automation Triggers</DialogTitle>
        </DialogHeader>

        <div className="flex max-h-[60vh] flex-col gap-4 overflow-y-auto">
          {isLoading &&
            triggerTypes.map((triggerType) => <Skeleton key={triggerType} className="h-[72px] w-full rounded-xl" />)}
          {isError && (
            <div className="flex flex-col items-center gap-3 rounded-xl border border-secondary bg-primary p-6 text-center">
              <p className="text-sm font-medium text-secondary">Couldn't load automations</p>
              <Button color="secondary" size="sm" onClick={() => refetch()}>
                Try again
              </Button>
            </div>
          )}
          {automations &&
            triggerTypes.map((triggerType) => {
              const automation = automations.find((a) => a.triggerType === triggerType);
              return (
                <TriggerSection
                  key={triggerType}
                  triggerType={triggerType}
                  automation={automation}
                  enabled={enabledByTrigger[triggerType] ?? automation?.enabled ?? false}
                  onToggle={(next) => saveToggle(triggerType, next)}
                />
              );
            })}
        </div>

        <DialogFooter className="gap-6">
          {editDisabledReason ? (
            <Tooltip title={editDisabledReason}>
              <TooltipTrigger>
                <span>{editButton}</span>
              </TooltipTrigger>
            </Tooltip>
          ) : (
            editButton
          )}
          <Button color="primary" onClick={() => onOpenChange(false)}>
            Done
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
